// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import {
  useRidehails,
  isPickupWithinLastDay,
  RidehailsTable,
  RidehailsControls,
  RidehailsMetrics,
  type RidehailMetric,
} from '@hopdrive/feature-ridehails';
import { generateRidehailsCSV } from '@services/csvService';
import dayjs from 'dayjs';

import {
  Box,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import { BiDownload } from 'react-icons/bi';

import Loading from '@components/Utils/Loading';
import Spacer from '@components/Utils/Spacer';
import ClearFiltersButton from '@components/Utils/ClearFiltersButton';
import { useShellTabActions } from '@features/products/shell/shellTabActions';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import { css } from '@emotion/css';

// COMPONENT ---------------------------------------------------------------- //

interface RideSharesTabContentProps {
  refetch: () => void;
}

const RideSharesTabContent: React.FC<RideSharesTabContentProps> = ({ refetch }) => {
  const cls = useStyles();
  const { loading, data, filteredData, filters, updateFilters } = useRidehails();

  // State for controlling date pickers
  const [disablePickers, setDisablePickers] = React.useState(filters.range !== 'custom');

  // Calculate counts for metrics from ALL data (not filtered)
  const counts = React.useMemo(() => {
    // Filter by date range only for counts
    let dateFiltered = [...data];
    if (filters.fromDate || filters.toDate) {
      const fromDate = filters.fromDate ? dayjs(filters.fromDate) : null;
      const toDate = filters.toDate ? dayjs(filters.toDate) : null;

      dateFiltered = dateFiltered.filter(ridehail => {
        const dateStr = ridehail?.created_at || ridehail?.move?.ready_by;
        if (!dateStr) return false;

        const date = dayjs(dateStr);
        if (fromDate && date.isBefore(fromDate)) return false;
        if (toDate && date.isAfter(toDate)) return false;
        return true;
      });
    }

    const allRides = dateFiltered.length;

    // Split 'Not Called' into 'Pending' (within last day) and 'Not Called' (too old)
    const allNotCalled = dateFiltered.filter(r => r?.status === 'Not Called');
    const pendingRides = allNotCalled.filter(r => {
      const pickupTime = r?.pickup_at || r?.move?.ready_by;
      return pickupTime && isPickupWithinLastDay(pickupTime);
    }).length;
    const notCalledRides = allNotCalled.filter(r => {
      const pickupTime = r?.pickup_at || r?.move?.ready_by;
      return !pickupTime || !isPickupWithinLastDay(pickupTime);
    }).length;
    // Completed statuses from actual ridehail statuses
    const completedRides = dateFiltered.filter(r => {
      const status = r?.status?.toLowerCase();
      return (
        status === 'completed' ||
        status === 'droppedoff' ||
        status === 'dropped_off' ||
        r?.move?.status === 'delivery successful'
      );
    }).length;
    // Active statuses from actual ridehail statuses
    const activeRides = dateFiltered.filter(r => {
      const status = r?.status?.toLowerCase();
      return (
        status === 'pending' ||
        status === 'booking' ||
        status === 'booked' ||
        status === 'driver_assigned' ||
        status === 'driver_arrived' ||
        status === 'in_progress'
      ) && !r?.move?.cancel_status;
    }).length;
    // Failed statuses from actual ridehail statuses
    const failedRides = dateFiltered.filter(r => {
      const status = r?.status?.toLowerCase();
      return (
        status === 'failed' ||
        status === 'retry'
      );
    }).length;
    // Canceled statuses from actual ridehail statuses
    const canceledRides = dateFiltered.filter(r => {
      const status = r?.status?.toLowerCase();
      return (
        r?.move?.cancel_status ||
        status === 'cancelled' ||
        status === 'canceled'
      );
    }).length;

    return {
      allRides,
      pendingRides,
      notCalledRides,
      activeRides,
      completedRides,
      failedRides,
      canceledRides,
    };
  }, [data, filters.fromDate, filters.toDate]);

  // Get unique providers
  const providersPool = React.useMemo(() => {
    return [...new Set(data.map(r => r?.provider).filter((provider): provider is string => !!provider))];
  }, [data]);

  const handleRangeChange = (range: string) => {
    let start = dayjs().startOf('day').toISOString();
    let end = dayjs().endOf('day').toISOString();

    switch (range) {
      case 'tomorrow':
        start = dayjs().add(1, 'day').startOf('day').toISOString();
        end = dayjs().add(1, 'day').endOf('day').toISOString();
        break;
      case 'today':
        start = dayjs().startOf('day').toISOString();
        end = dayjs().endOf('day').toISOString();
        break;
      case 'yesterday':
        start = dayjs().subtract(1, 'day').startOf('day').toISOString();
        end = dayjs().subtract(1, 'day').endOf('day').toISOString();
        break;
      case 'nextweek':
        start = dayjs().add(1, 'week').startOf('week').toISOString();
        end = dayjs().add(1, 'week').endOf('week').toISOString();
        break;
      case 'thisweek':
        start = dayjs().startOf('week').toISOString();
        end = dayjs().endOf('week').toISOString();
        break;
      case 'lastweek':
        start = dayjs().subtract(1, 'week').startOf('week').toISOString();
        end = dayjs().subtract(1, 'week').endOf('week').toISOString();
        break;
      case 'nextmonth':
        start = dayjs().add(1, 'month').startOf('month').toISOString();
        end = dayjs().add(1, 'month').endOf('month').toISOString();
        break;
      case 'thismonth':
        start = dayjs().startOf('month').toISOString();
        end = dayjs().endOf('month').toISOString();
        break;
      case 'lastmonth':
        start = dayjs().subtract(1, 'month').startOf('month').toISOString();
        end = dayjs().subtract(1, 'month').endOf('month').toISOString();
        break;
      case 'custom':
        setDisablePickers(false);
        updateFilters({ range });
        return;
      default:
        break;
    }

    setDisablePickers(range !== 'custom');
    updateFilters({
      range,
      fromDate: start,
      toDate: end,
    });
  };

  const handleDateChange = (type: 'start' | 'end') => (date: string) => {
    if (date) {
      if (type === 'start') {
        updateFilters({ fromDate: dayjs(date).startOf('day').toISOString() });
      } else {
        updateFilters({ toDate: dayjs(date).endOf('day').toISOString() });
      }
    }
  };

  const handleMetricChange = (newMetric: RidehailMetric) => {
    updateFilters({ metric: newMetric });
  };

  const handleClearFilters = () => {
    handleRangeChange('thisweek');
    updateFilters({
      searchText: '',
      provider: '',
      metric: 'all',
    });
  };

  // The tab's contribution to the header: its refresh, and Export CSV in the overflow.
  const nothingToExport = filteredData.length === 0;
  const tabActions = React.useMemo(
    () => ({
      sync: refetch,
      overflow: [
        {
          key: 'export-csv',
          label: 'Export CSV',
          icon: <BiDownload />,
          onSelect: () => generateRidehailsCSV(filteredData, filters.fromDate, filters.toDate),
          disabledReason: nothingToExport ? 'No rideshares to export' : undefined,
        },
      ],
    }),
    [refetch, filteredData, filters.fromDate, filters.toDate, nothingToExport]
  );
  useShellTabActions(tabActions);

  if (loading) {
    return <Loading />;
  }

  return (
    <Box className={cls.container}>
      {loading && <LinearProgress />}

      {/* Controls; Clear Filters closes the filter set. */}
      <RidehailsControls
        range={filters.range}
        start={filters.fromDate}
        end={filters.toDate}
        disablePickers={disablePickers}
        provider={filters.provider}
        search={filters.searchText}
        providersPool={providersPool}
        setProvider={provider => updateFilters({ provider })}
        setSearch={search => updateFilters({ searchText: search })}
        onRangeChange={handleRangeChange}
        onDateChange={handleDateChange}
        trailing={<ClearFiltersButton onClear={handleClearFilters} />}
      />

      <Spacer size={2} />

      {/* Metrics */}
      <RidehailsMetrics
        loading={loading}
        counts={counts}
        metric={filters.metric}
        onMetricChange={handleMetricChange}
      />

      <Spacer size={2} />

      {/* Table */}
      {filteredData.length === 0 ? (
        <DefaultFallback
          fullscreen
          severity='warning'
          message='No rideshares found with selected filters.'
        />
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <RidehailsTable ridehails={filteredData} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = () => ({
  container: css`
    padding: 24px;
    height: 100%;
    overflow-y: auto;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default RideSharesTabContent;